import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Gamepad2, Shield, Wallet, Trophy, Users, 
  ChevronRight, Star, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Marquee from 'react-fast-marquee';
import { MatchStakeIcon } from '../components/MatchStakeLogo';
import { AnnouncementBanner } from '../components/AnnouncementDisplay';

const Landing = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const features = [
    { icon: Shield, title: 'Secure Platform', desc: 'Safe and verified match system' },
    { icon: Wallet, title: 'Platform-Funded Rewards', desc: 'Winners receive rewards funded by MatchStake' },
    { icon: Trophy, title: 'Skill-Based', desc: 'Compete based on skill, not luck' },
    { icon: Users, title: 'P2P Matches', desc: 'Challenge any player, anytime' },
  ];

  const games = [
    { name: 'Madden NFL', color: '#FF6B35' },
    { name: 'NBA 2K', color: '#FF2E2E' },
    { name: 'EA Sports FC', color: '#00FF94' },
    { name: 'Call of Duty', color: '#FFB800' },
    { name: 'Fortnite', color: '#7000FF' },
    { name: 'Rocket League', color: '#00F0FF' },
  ];

  const recentWinners = [
    { name: 'xKiller99', amount: 250, game: 'Madden NFL' },
    { name: 'ProGamer_X', amount: 180, game: 'NBA 2K' },
    { name: 'ShadowStrike', amount: 420, game: 'Call of Duty' },
    { name: 'VictoryLap', amount: 95, game: 'Fortnite' },
    { name: 'ElitePlayer', amount: 310, game: 'EA Sports FC' },
    { name: 'ChampionRise', amount: 175, game: 'Rocket League' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Announcement Banner */}
      <AnnouncementBanner />
      {/* Hero Glow Background */}
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MatchStakeIcon className="w-8 h-8 sm:w-10 sm:h-10" />
            <span className="font-heading font-bold text-base sm:text-xl tracking-tight">MATCHSTAKE</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-white/70 hover:text-white transition-colors">Features</a>
            <a href="#games" className="text-white/70 hover:text-white transition-colors">Games</a>
            <a href="#how-it-works" className="text-white/70 hover:text-white transition-colors">How It Works</a>
          </div>
          <Button 
            onClick={handleGetStarted}
            className="btn-primary clip-slant-sm px-3 sm:px-6 text-xs sm:text-sm"
            data-testid="nav-get-started-btn"
          >
            {user ? 'Dashboard' : 'Get Started'}
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#00FF94] animate-pulse" />
                <span className="text-sm text-white/70">Live matches happening now</span>
              </div>
              
              <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tighter leading-[0.9] mb-6">
                <span className="text-white">COMPETE.</span>
                <br />
                <span className="text-[#00F0FF] glow-cyan">CHALLENGE.</span>
                <br />
                <span className="text-[#CCFF00] glow-green">WIN.</span>
              </h1>
              
              <p className="text-lg text-white/60 max-w-lg mb-8 leading-relaxed">
                Compete in head-to-head gaming matches. Challenge players, win money, 
                and withdraw your winnings. Madden, NBA 2K, and more.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleGetStarted}
                  size="lg"
                  className="btn-primary clip-slant px-8 py-6 text-lg"
                  data-testid="hero-get-started-btn"
                >
                  Start Competing
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="btn-secondary px-8 py-6 text-lg"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-learn-more-btn"
                >
                  Learn More
                </Button>
              </div>

              <div className="flex items-center gap-8 mt-10 pt-10 border-t border-white/10">
                <div>
                  <p className="font-mono text-3xl font-bold text-[#CCFF00] glow-green">$2.4M+</p>
                  <p className="text-sm text-white/50">Winnings Paid</p>
                </div>
                <div>
                  <p className="font-mono text-3xl font-bold text-[#00F0FF] glow-cyan">15K+</p>
                  <p className="text-sm text-white/50">Active Players</p>
                </div>
                <div>
                  <p className="font-mono text-3xl font-bold text-white">50K+</p>
                  <p className="text-sm text-white/50">Matches Played</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1648943017805-f3869e04e25d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Gaming setup"
                  className="rounded-2xl w-full object-cover aspect-square"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent rounded-2xl" />
                
                {/* Floating card */}
                <motion.div 
                  className="absolute -bottom-6 -left-6 glass-card rounded-xl p-4 gradient-border"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#00FF94]/20 flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-[#00FF94]" />
                    </div>
                    <div>
                      <p className="text-xs text-white/50">Latest Win</p>
                      <p className="font-mono font-bold text-[#CCFF00]">+$420.00</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Winners Marquee - Overlapping with hero */}
      <section className="relative -mt-10 mb-20">
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-[#050505] z-10 pointer-events-none" />
        <Marquee speed={40} gradient={false} className="py-4 bg-[#0A0A0B]/80 backdrop-blur-sm border-y border-white/5">
          {recentWinners.concat(recentWinners).map((winner, i) => (
            <div key={i} className="flex items-center gap-3 mx-8">
              <Star className="w-4 h-4 text-[#FFB800]" />
              <span className="text-white/70">{winner.name}</span>
              <span className="font-mono text-[#CCFF00] font-bold">+${winner.amount}</span>
              <span className="text-white/40 text-sm">{winner.game}</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Why <span className="text-[#00F0FF]">MatchStake</span>?
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Built for competitive gamers who want to put their skills to the test
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-xl p-6 hover:border-white/20 transition-all group"
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center mb-4 group-hover:bg-[#00F0FF]/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-white/50 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Games Section */}
      <section id="games" className="py-20 px-6 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Supported <span className="text-[#7000FF]">Games</span>
            </h2>
            <p className="text-white/50">Plus custom game support for any title</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {games.map((game, i) => (
              <motion.div
                key={game.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ scale: 1.05 }}
                className="glass-card rounded-xl p-6 text-center cursor-pointer hover:border-white/20 transition-all"
                style={{ borderColor: `${game.color}20` }}
                data-testid={`game-${game.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Gamepad2 className="w-8 h-8 mx-auto mb-3" style={{ color: game.color }} />
                <p className="font-semibold text-sm">{game.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
              How It <span className="text-[#CCFF00]">Works</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Create or Join', desc: 'Set up a match with your entry fee or accept an open challenge', icon: Gamepad2 },
              { step: '02', title: 'Play & Prove', desc: 'Complete your match and submit screenshot proof of the result', icon: Shield },
              { step: '03', title: 'Get Rewarded', desc: 'Winner receives a competition reward from the platform', icon: Wallet },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative"
                data-testid={`step-${item.step}`}
              >
                <div className="glass-card rounded-xl p-8">
                  <span className="font-mono text-6xl font-bold text-white/5 absolute top-4 right-6">{item.step}</span>
                  <div className="w-14 h-14 rounded-xl bg-[#7000FF]/20 flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-[#7000FF]" />
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3">{item.title}</h3>
                  <p className="text-white/50">{item.desc}</p>
                </div>
                {i < 2 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-6 w-8 h-8 text-white/20 -translate-y-1/2" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-12 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 hero-glow opacity-50" />
            <div className="relative z-10">
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Ready to <span className="text-[#00F0FF] glow-cyan">Compete</span>?
              </h2>
              <p className="text-white/50 max-w-lg mx-auto mb-8">
                Join thousands of players competing in head-to-head matches. 
                Sign up in seconds with Google.
              </p>
              <Button 
                onClick={handleGetStarted}
                size="lg"
                className="btn-primary clip-slant px-10 py-6 text-lg"
                data-testid="cta-get-started-btn"
              >
                {user ? 'Go to Dashboard' : 'Get Started Now'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-white/10 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Compliance Statement */}
          <div className="text-center mb-8 p-4 rounded-xl bg-[#00FF94]/5 border border-[#00FF94]/20">
            <p className="text-[#00FF94]/80 text-sm font-medium">
              Deposit money, play matches, win payouts, withdraw anytime.
            </p>
          </div>
          
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-2">
              <MatchStakeIcon className="w-8 h-8" />
              <span className="font-heading font-bold">MATCHSTAKE</span>
            </div>
            
            {/* Policy Links - Two rows for better visibility */}
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
              <a href="/terms" className="text-white/50 hover:text-white text-sm transition-colors">Terms of Service</a>
              <a href="/privacy" className="text-white/50 hover:text-white text-sm transition-colors">Privacy Policy</a>
              <a href="/withdrawal-policy" className="text-white/50 hover:text-white text-sm transition-colors">Withdrawal Policy</a>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
              <a href="/match-rules" className="text-white/50 hover:text-white text-sm transition-colors">Match Rules</a>
              <a href="/faq" className="text-white/50 hover:text-white text-sm transition-colors">FAQ</a>
              <a href="/contact" className="text-white/50 hover:text-white text-sm transition-colors">Contact</a>
            </div>
            
            <p className="text-white/40 text-sm mt-4">
              © 2026 MatchStake. All rights reserved. 18+ only.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
